import { useLiveData } from '@/hooks/useLiveData';
import { getReferenceString } from '@medplum/core';
import { DocumentReference, OperationOutcome, Patient } from '@medplum/fhirtypes';
import { useCallback, useMemo, useState } from 'react';

export interface UseDocumentsResult {
  documents: DocumentReference[];
  documentsLoading: boolean;
  documentsError: OperationOutcome | Error | undefined;
  refreshDocuments: () => void;
  pauseLiveUpdates: () => void;
}

export function useDocuments(patient: Patient | undefined): UseDocumentsResult {
  const [areLiveUpdatesPaused, setAreLiveUpdatesPaused] = useState(false);

  const queryParams = useMemo(() => {
    if (!patient) return undefined;

    const defaultParams: Record<string, any> = {
      subject: getReferenceString(patient),
      _sort: '-date',
      'contenttype:not': 'application/json',
    };

    return defaultParams;
  }, [patient]);

  const { data, loading, error, refresh } = useLiveData('DocumentReference', queryParams, {
    pauseLiveUpdates: areLiveUpdatesPaused,
  });

  const pauseLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(true);
  }, []);

  const refreshDocuments = useCallback(() => {
    setAreLiveUpdatesPaused(false);
    refresh();
  }, [refresh]);

  return {
    documents: data,
    documentsLoading: loading,
    documentsError: error,
    refreshDocuments,
    pauseLiveUpdates,
  };
}
